import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPath(window.location.pathname);
    }
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
    <header data-section-id="14025" id="site-navigation" className={cn("fixed top-0 left-0 right-0 z-50 w-full border-b transition-all duration-500", isScrolled ? "bg-background/60 border-border/80 backdrop-blur-xl shadow-xl shadow-primary/5" : "bg-background border-border/40 shadow-none")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <Link to="/HomePage" className="flex items-center gap-2 outline-none">
          <img src="https://brief-horse.websiteshop.au/wp-content/uploads/2026/03/Power-Shine-Solar-2.png" alt="Power Shine Solar Cleaning" className="h-20 w-auto object-contain brightness-110" />
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8">
          {loading ? <div className="flex gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 animate-pulse rounded-full bg-muted/60" />)}
            </div> : <NavigationMenu>
              <NavigationMenuList className="gap-6">
                {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className={cn("group relative text-sm font-bold tracking-tight transition-colors hover:text-primary", currentPath === item.href ? "text-primary" : "text-foreground/80")}>
                        {item.label}
                        <span className={cn("absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full", currentPath === item.href ? "w-full" : "")} />
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        {}
        <div className="flex items-center gap-4">
          {}
          <SafeHydrate><Drawer direction="top">
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-secondary rounded-full" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="h-[100dvh] max-h-[100dvh] rounded-none border-none bg-background/95 backdrop-blur-2xl">
              <div className="container mx-auto flex h-full flex-col px-4 py-6">
                <div className="flex items-center justify-between mb-8">
                  <Link to="/HomePage" className="flex items-center">
                    <img src="https://brief-horse.websiteshop.au/wp-content/uploads/2026/03/Power-Shine-Solar-2.png" alt="Power Shine Solar Cleaning" className="h-20 w-auto brightness-110" />
                  </Link>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <X className="h-6 w-6" />
                    </Button>
                  </DrawerClose>
                </div>

                <div className="sr-only">
                  <DrawerTitle>Mobile Navigation</DrawerTitle>
                  <DrawerDescription>
                    Main site navigation menu for Power Shine
                  </DrawerDescription>
                </div>

                <nav className="flex flex-col gap-6 text-center mt-8">
                  {loading ? <div className="flex flex-col gap-4 items-center">
                      {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-40 animate-pulse rounded-full bg-muted/60" />)}
                    </div> : menuItems.map((item, i) => <DrawerClose key={item.id} asChild data-index={i}>
                        <Link to={item.href || "#"} className="text-3xl font-bold tracking-tighter text-foreground hover:text-primary transition-colors">
                          {item.label}
                        </Link>
                      </DrawerClose>)}
                </nav>

                <div className="mt-auto flex flex-col gap-4 pb-8">
                  <div className="flex justify-center items-center gap-2 text-muted-foreground font-medium">
                    <Phone className="h-5 w-5 text-primary" />
                    <span className="text-lg">0466 880 255</span>
                  </div>
                </div>
              </div>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </header>
    <div className="h-20" />
    </>;
}
export default function Header() {
  return <MenuProvider menu_id="2">
      <HeaderContent />
    </MenuProvider>;
}